/**
 * ContentStrategistFlow — UI for the "Handle my week" meta-agent.
 *
 * Light theme matching OrchestratorPage (white / violet / gray-50).
 * Shows:
 *  1. Phase stepper (planning → generating → reviewing → complete)
 *  2. Calendar plan viewer
 *  3. Child pipeline progress
 *  4. Aggregate quality dashboard
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Brain,
  Calendar,
  Check,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Zap,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────

interface CalendarEntry {
  day: number;
  theme?: string;
  posts?: Array<{
    platform?: string;
    topic?: string;
    caption?: string;
    hook?: string;
    post_type?: string;
  }>;
}

interface ChildPipeline {
  id: string;
  status: string;
  quality_score: number;
  topic: string;
  platforms: string[];
  iteration_count: number;
}

interface ContentStrategistFlowProps {
  strategistStatus: string; // planning | generating | reviewing | completed | failed
  calendarPlan: CalendarEntry[];
  childPipelines: ChildPipeline[];
  qualityScore: number;
  costUsd: number;
  errors: string[];
}

// ── Status helpers ─────────────────────────────────────────────────

function phaseStatus(
  phase: string,
  currentStatus: string,
): 'pending' | 'running' | 'completed' | 'failed' {
  const order = ['planning', 'generating', 'reviewing', 'completed'];
  const currentIdx = order.indexOf(currentStatus);
  const phaseIdx = order.indexOf(phase);

  if (currentStatus === 'failed') {
    return phaseIdx <= currentIdx ? 'failed' : 'pending';
  }
  if (phaseIdx < currentIdx) {
    return 'completed';
  }
  if (phaseIdx === currentIdx) {
    return currentStatus === 'completed' ? 'completed' : 'running';
  }
  return 'pending';
}

// ── Component ──────────────────────────────────────────────────────

const ContentStrategistFlow: React.FC<ContentStrategistFlowProps> = ({
  strategistStatus,
  calendarPlan,
  childPipelines,
  qualityScore,
  costUsd,
  errors,
}) => {
  const { t } = useTranslation();
  const [expandedDay, setExpandedDay] = React.useState<number | null>(null);

  const phases = [
    { id: 'planning', label: t('strategist_phase_planning', 'Planning Calendar'), icon: Brain },
    { id: 'generating', label: t('strategist_phase_generating', 'Generating Posts'), icon: Zap },
    { id: 'reviewing', label: t('strategist_phase_reviewing', 'Quality Review'), icon: TrendingUp },
    { id: 'completed', label: t('strategist_phase_complete', 'Complete'), icon: Check },
  ];

  const completedChildren = childPipelines.filter((c) => c.status === 'completed');
  const failedChildren = childPipelines.filter((c) => c.status === 'failed');
  const avgScore =
    completedChildren.length > 0
      ? completedChildren.reduce((s, c) => s + c.quality_score, 0) / completedChildren.length
      : qualityScore;

  return (
    <div className="w-full space-y-4">
      {/* ── Phase stepper ── */}
      <div className="rounded-xl border border-gray-100 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-bold text-gray-800">
            {t('strategist_title', 'Content Strategist')}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {phases.map((phase, idx) => {
            const ps = phaseStatus(phase.id, strategistStatus);
            const Icon = phase.icon;

            return (
              <React.Fragment key={phase.id}>
                {idx > 0 && (
                  <div
                    className={`flex-1 h-0.5 rounded-full ${
                      ps === 'completed' || ps === 'running'
                        ? 'bg-violet-300'
                        : 'bg-gray-100'
                    }`}
                  />
                )}
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`
                    w-8 h-8 rounded-full flex items-center justify-center transition-all
                    ${ps === 'running' ? 'bg-violet-100 ring-2 ring-violet-300' : ''}
                    ${ps === 'completed' ? 'bg-emerald-100' : ''}
                    ${ps === 'failed' ? 'bg-red-100' : ''}
                    ${ps === 'pending' ? 'bg-gray-100' : ''}
                  `}
                  >
                    {ps === 'running' ? (
                      <Loader2 className="w-3.5 h-3.5 text-violet-500 animate-spin" />
                    ) : ps === 'completed' ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    ) : ps === 'failed' ? (
                      <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                    ) : (
                      <Icon className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </div>
                  <span
                    className={`text-xs whitespace-nowrap ${
                      ps === 'running'
                        ? 'text-violet-600 font-medium'
                        : ps === 'completed'
                        ? 'text-emerald-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {phase.label}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Calendar plan ── */}
      {calendarPlan.length > 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-cyan-500" />
            <span className="text-sm font-bold text-gray-800">
              {t('strategist_calendar', 'Content Calendar')}
            </span>
            <span className="text-xs text-gray-400 font-medium">
              {calendarPlan.length} {t('strategist_days', 'days')}
            </span>
          </div>

          <div className="space-y-1.5">
            {calendarPlan.map((day) => (
              <div key={day.day} className="border border-gray-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-xs text-violet-600 font-bold">
                      {day.day}
                    </span>
                    <span className="text-sm text-gray-700 font-medium">
                      {day.theme || `Day ${day.day}`}
                    </span>
                    <span className="text-xs text-gray-400">
                      {(day.posts || []).length} {t('strategist_posts', 'posts')}
                    </span>
                  </div>
                  {expandedDay === day.day ? (
                    <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </button>

                {expandedDay === day.day && (day.posts || []).length > 0 && (
                  <div className="px-3 pb-2 space-y-1">
                    {(day.posts || []).map((post, pi) => (
                      <div
                        key={pi}
                        className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-md text-xs"
                      >
                        {post.platform && (
                          <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded font-medium">
                            {post.platform}
                          </span>
                        )}
                        <span className="text-gray-600 truncate">
                          {post.topic || post.caption || post.hook || 'Post'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Child pipeline progress ── */}
      {childPipelines.length > 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-bold text-gray-800">
                {t('strategist_pipelines', 'Post Pipelines')}
              </span>
            </div>
            <span className="text-xs text-gray-400 font-medium">
              {completedChildren.length}/{childPipelines.length}{' '}
              {t('strategist_complete', 'complete')}
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden mb-3">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-700"
              style={{
                width: `${
                  childPipelines.length > 0
                    ? (completedChildren.length / childPipelines.length) * 100
                    : 0
                }%`,
              }}
            />
          </div>

          {/* Pipeline list */}
          <div className="space-y-1">
            {childPipelines.map((child) => (
              <div
                key={child.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs"
              >
                {child.status === 'completed' ? (
                  <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                ) : child.status === 'failed' ? (
                  <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                ) : child.status === 'running' ? (
                  <Loader2 className="w-3.5 h-3.5 text-violet-500 animate-spin flex-shrink-0" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full bg-gray-200 flex-shrink-0" />
                )}
                <span className="text-gray-700 truncate flex-1">{child.topic}</span>
                {child.platforms.map((p) => (
                  <span
                    key={p}
                    className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-md"
                  >
                    {p}
                  </span>
                ))}
                {child.quality_score > 0 && (
                  <span
                    className={`font-mono font-bold ${
                      child.quality_score >= 7
                        ? 'text-emerald-600'
                        : child.quality_score >= 5
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}
                  >
                    {child.quality_score.toFixed(1)}
                  </span>
                )}
                {child.iteration_count > 0 && (
                  <span className="text-amber-600 font-semibold">
                    &times;{child.iteration_count + 1}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Aggregate stats ── */}
      {strategistStatus === 'completed' && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-emerald-600">{completedChildren.length}</div>
              <div className="text-xs text-gray-500">
                {t('strategist_stat_posts', 'Posts Created')}
              </div>
            </div>
            <div>
              <div
                className={`text-lg font-bold ${
                  avgScore >= 7
                    ? 'text-emerald-600'
                    : avgScore >= 5
                    ? 'text-amber-600'
                    : 'text-red-600'
                }`}
              >
                {avgScore.toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">
                {t('strategist_stat_avg_score', 'Avg Score')}
              </div>
            </div>
            <div>
              <div className="text-lg font-bold text-red-500">{failedChildren.length}</div>
              <div className="text-xs text-gray-500">
                {t('strategist_stat_failed', 'Failed')}
              </div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-700">${costUsd.toFixed(3)}</div>
              <div className="text-xs text-gray-500">
                {t('strategist_stat_cost', 'Total Cost')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Errors ── */}
      {errors.length > 0 && (
        <div className="p-3.5 rounded-xl bg-red-50 border border-red-200">
          <div className="flex items-center gap-2 text-sm text-red-700 font-semibold mb-1">
            <AlertCircle className="w-4 h-4" />
            {t('strategist_errors', 'Issues')}
          </div>
          {errors.map((err, i) => (
            <div key={i} className="text-xs text-red-500 ml-6">
              {err}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentStrategistFlow;
